import React, { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faPenToSquare,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";
import {
  getEditPageSection,
  getEditSectionPlace,
  removeSection,
} from "services/apiService";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Tooltip,
  CardContent,
  Typography,
  CardMedia,
  Card,
  Box,
  Skeleton,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { fetchSectionsDatas } from "../../redux/Slice/getSectionDataSlice";
import JsonSectionEdit from "./SectionEditData/JsonSectionEdit";
import config from "config";

const DraggableRow = ({ section, setRefreshData, pageid, isLoading }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const dispatch = useDispatch();
  const AppUrl = config.REACT_APP_IMAGE_URL;
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [sectionName, setSectionName] = useState("");

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: "Want to delete the Section?",
      text: "This action will delete the section permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const payload = {
          page_id: parseInt(pageid),
          section_id: parseInt(section.id.match(/\d+/)[0]),
          section_group: parseInt(section.section_group),
        };
        const response = await removeSection(payload);

        if (response.status_code === 200) {
          await Swal.fire(
            "Deleted!",
            "Your section has been deleted.",
            "success"
          );
          setRefreshData((prev) => prev + 1);
        } else {
          toast.error("Section deletion failed. Please try again.");
        }
      } catch {
        toast.error("Section deletion failed. Please try again.");
      }
    }
  };

  const handleEdit = (sectionGroupId, pageIds, sectionName) => {
    dispatch(fetchSectionsDatas({ sectionGroupId, pageId: parseInt(pageIds) }));
    setIsEditModalOpen(true);
    setSectionName(sectionName);
  };

  return (
    <>
      {isLoading ? (
        <TableRow>
          <TableCell
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "10px",
              gap: "10px",
            }}
          >
            <Skeleton variant="rectangular" height={170} width="100%" />
            <Skeleton variant="text" width="60%" height={24} sx={{ mt: 1 }} />
          </TableCell>
        </TableRow>
      ) : (
        <TableRow
          ref={setNodeRef}
          style={{
            transform: CSS.Transform.toString(transform),
            transition: transition || "transform 250ms ease",
            color: "black",
            boxShadow: isDragging ? "0px 4px 10px rgba(0, 0, 0, 0.2)" : "none",
          }}
        >
          <TableCell style={{ textAlign: "center", padding: "8px" }}>
            <IconButton
              {...attributes}
              {...listeners}
              sx={{
                cursor: "grab",
                color: "black",
                borderRadius: "0px",
                fontSize: "14px",
                backgroundColor: "rgb(255, 215, 0)",
                "&:hover": {
                  backgroundColor: "rgb(218, 165, 32)",
                },
              }}
            >
              <FontAwesomeIcon icon={faBars} />
            </IconButton>
          </TableCell>

          <TableCell>
            <Card
              sx={{
                position: "relative",
                overflow: "hidden",
                height: "100%",
                "&:hover .hover-overlay": {
                  opacity: 1,
                  transform: "translateY(0) !important",
                },
              }}
            >
              <CardMedia
                component="img"
                image={`${AppUrl}${section.previewSection}`}
                alt="media"
                sx={{
                  height: 170,
                  objectFit: "contain",
                  backgroundColor: "#f5f5f5",
                  transition: "filter 0.3s ease",
                }}
              />
              <CardContent>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    fontWeight: 500,
                    padding: "8px",
                    color: "#333",
                    backgroundColor: "#f5f5f5",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {section?.title
                    ?.replace(/_/g, " ")
                    .replace(/section/i, "Section")
                    .replace(/(\d+)/, (match) => ` ${match}`)
                    .trim()}
                </Typography>
              </CardContent>

              <Box
                className="hover-overlay"
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  background: "rgba(0, 0, 0, 0.7)",
                  display: "flex",
                  justifyContent: "center",
                  gap: "15px",
                  padding: "12px",
                  opacity: 0,
                  transition:
                    "opacity 0.4s ease, transform 0.4s ease !important",
                  transform: "translateY(100%) !important",
                  zIndex: 3,
                }}
              >
                <Tooltip title="Edit Section" arrow>
                  <FontAwesomeIcon
                    icon={faPenToSquare}
                    style={{
                      color: "white",
                      fontSize: "20px",
                      cursor: "pointer",
                      padding: "8px",
                      transition: "0.3s ease",
                    }}
                    onClick={() =>
                      handleEdit(section.section_group, pageid, section.title)
                    }
                    onMouseEnter={(e) => (e.currentTarget.style.color = "blue")}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.color = "white")
                    }
                  />
                </Tooltip>
                <Tooltip title="Remove" arrow>
                  <FontAwesomeIcon
                    icon={faTrash}
                    style={{
                      color: "white",
                      fontSize: "20px",
                      cursor: "pointer",
                      padding: "8px",
                      transition: "0.3s ease",
                    }}
                    onClick={handleDelete}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "red")}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.color = "white")
                    }
                  />
                </Tooltip>
              </Box>
            </Card>
          </TableCell>
        </TableRow>
      )}

      <JsonSectionEdit
        isOpen={isEditModalOpen}
        onRequestClose={() => setIsEditModalOpen(false)}
        sectionId={section.id}
        sectionGroupId={section.section_group}
        sectionName={sectionName.toLowerCase()}
      />
    </>
  );
};

const KanbanBoard = ({ name, pageid, modalIsOpen, sectionImages }) => {
  const [sections, setSections] = useState([]);
  const [isLoading, setisLoading] = useState(true);
  const [refreshData, setRefreshData] = useState(0);
  const mergedSections = sections.map((section) => {
    const matchingImage = sectionImages.find(
      (image) =>
        image.section_name.trim().toLowerCase() ===
        section.title.trim().toLowerCase()
    );
    return {
      ...section,
      previewSection: matchingImage ? matchingImage.previewSection : null,
    };
  });

  useEffect(() => {
    const fetchSections = async () => {
      setisLoading(true);

      try {
        const response = await getEditPageSection(pageid);
        if (response?.data) {
          setSections(
            response.data.map((section, index) => ({
              id: `section${section.section_id}_${index}`,
              title: section.section_name.toUpperCase(),
              section_group: section.section_group,
            }))
          );
        } else {
          setSections([]);
        }
      } catch (error) {
        console.error("Error fetching sections:", error);
        setSections([]);
      } finally {
        setTimeout(() => {
          setisLoading(false);
        }, 500);
      }
    };

    fetchSections();
  }, [refreshData, pageid, modalIsOpen]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setSections((prevSections) => {
      const oldIndex = prevSections.findIndex((item) => item.id === active.id);
      const newIndex = prevSections.findIndex((item) => item.id === over.id);
      return arrayMove(prevSections, oldIndex, newIndex);
    });
  };

  const saveKanbanChanges = async () => {
    try {
      const payload = {
        page_id: parseInt(pageid),
        section_place: sections.map((item, index) => ({
          section_group: item.section_group,
          place_order: index + 1,
        })),
      };
      const response = await getEditSectionPlace(payload);
      if (response.status_code === 200) {
        toast.success("Section place changed successfully!");
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <TableContainer
        component={Paper}
        style={{
          height: "65vh",
          overflow: "auto",
        }}
      >
        <Table>
          <TableHead
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <TableRow>
              <TableCell
                align="center"
                sx={{
                  fontWeight: "bold",
                  fontSize: "0.7rem",
                  color: "white",
                  padding: "12px",
                  textTransform: "uppercase",
                  backgroundColor: "#1976d2",
                }}
                colSpan={7}
              >
                {name} - page
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            <SortableContext
              items={sections.map((section) => section.id)}
              strategy={verticalListSortingStrategy}
            >
              {mergedSections.map((section) => (
                <DraggableRow
                  key={section.id}
                  section={section}
                  setRefreshData={setRefreshData}
                  pageid={pageid}
                  isLoading={isLoading}
                />
              ))}
            </SortableContext>
          </TableBody>
        </Table>
      </TableContainer>

      <div className=" mt-3 mb-3">
        <Button variant="outlined" onClick={saveKanbanChanges} fullWidth>
          Save Order
        </Button>
      </div>
    </DndContext>
  );
};

export default KanbanBoard;
